<template>
  <div
    :id="id"
    class="modal fade"
    data-keyboard="false"
    data-backdrop="static"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <modal-close-button @confirmed="closeModal" />
          <h4 class="modal-title">{{ titel || $t('global.gradeoverview') }}</h4>
        </div>
        <div class="modal-body">
          <!-- Datum und Textvariante nur für Zeugnisse/Diplome (Printoutdaten des Printouts) -->
          <div v-if="mitDatum" class="row mb-3">
            <div class="col-6">
              <div class="form-group">
                <date-picker
                  date-format="dd.mm.yy"
                  placeholder=" "
                  :initial="datum"
                  :show-icon="true"
                  :inModal="true"
                  @update="(val) => (datum = val)"
                />
                <label>{{ $t('global.date') }}</label>
              </div>
            </div>
          </div>
          <div v-if="mitDatum && varianten.length > 0" class="row mb-3">
            <div class="col-12">
              <div class="font-weight-bold mb-1">Variante:</div>
              <div
                v-for="variante in varianten"
                :key="variante.id"
                class="custom-control custom-radio mt-1"
              >
                <input
                  :id="`${id}-variante-${variante.id}`"
                  v-model="ausgewaehlteVariante"
                  class="custom-control-input"
                  type="radio"
                  :name="`${id}-variante`"
                  :value="variante.id"
                />
                <label
                  class="custom-control-label pointer ml-2"
                  :for="`${id}-variante-${variante.id}`"
                >
                  {{ variante.bezeichnung }}
                </label>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-12">
              <div class="font-weight-bold mb-1">Studienjahre:</div>
              <div
                v-for="block in bloecke"
                :key="block.index"
                class="custom-control custom-checkbox mt-1"
              >
                <input
                  :id="`${id}-block-${block.index}`"
                  v-model="ausgewaehlteBloecke"
                  class="custom-control-input"
                  type="checkbox"
                  :value="block.index"
                />
                <label
                  class="custom-control-label pointer ml-2"
                  :for="`${id}-block-${block.index}`"
                >
                  {{ block.bezeichnung }}
                  <span v-if="!block.hatNoten" class="text-muted">
                    (noch keine Noten)
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div v-if="!mitDatum" class="row mb-3">
            <div class="col-12">
              <div class="custom-control custom-checkbox">
                <input
                  :id="`${id}-ohne-note`"
                  v-model="ohneNoteAnzeigen"
                  class="custom-control-input"
                  type="checkbox"
                />
                <label class="custom-control-label pointer ml-2" :for="`${id}-ohne-note`">
                  Module, Fächer und Testate ohne Note anzeigen
                </label>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="font-weight-bold mb-1">{{ $t('global.type') }}:</div>
              <div
                v-for="option in aktionen"
                :key="option.key"
                class="custom-control custom-radio mt-1"
              >
                <input
                  :id="`${id}-aktion-${option.key}`"
                  v-model="aktion"
                  class="custom-control-input"
                  type="radio"
                  :name="`${id}-aktion`"
                  :value="option.key"
                />
                <label
                  class="custom-control-label pointer ml-2"
                  :for="`${id}-aktion-${option.key}`"
                >
                  {{ option.label }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-success"
            :disabled="
              ausgewaehlteBloecke.length === 0 ||
              (mitDatum && varianten.length > 0 && !ausgewaehlteVariante)
            "
            @click="ausgeben"
          >
            PDF ausgeben
          </button>
          <button type="button" class="btn btn-default" @click="closeModal">
            Schliessen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ModalCloseButton from '@/components/global/ModalCloseButton'
  import DatePicker from '@/components/Datepicker'
  import CarboneApi from '@/api/CarboneApi'
  import { modal } from '@/mixins/Modal'

  // publish-Wert pro Aktion: null = anzeigen, false = zuweisen, true = zuweisen & publizieren
  const PUBLISH = { anzeigen: null, zuweisen: false, publizieren: true }

  export default {
    name: 'NotenuebersichtModulModal',
    mixins: [modal],
    components: {
      ModalCloseButton,
      DatePicker,
    },
    props: {
      id: {
        type: String,
        default: 'notenuebersicht-modul-modal',
      },
      // [{ index, bezeichnung, hatNoten }]
      bloecke: {
        type: Array,
        default: () => [],
      },
      titel: {
        type: String,
        default: null,
      },
      // Zeugnisse/Diplome: Datum und Variante (Printoutdaten) statt "ohne Note anzeigen"
      mitDatum: {
        type: Boolean,
        default: false,
      },
      printout: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        ausgewaehlteBloecke: [],
        ohneNoteAnzeigen: false,
        aktion: 'anzeigen',
        datum: new Intl.DateTimeFormat('de-CH', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date()),
        printoutdaten: [],
        ausgewaehlteVariante: null,
      }
    },
    computed: {
      varianten() {
        if (!this.printout?.id) return []
        return this.printoutdaten.filter(
          (p) =>
            p.printout === this.printout.id &&
            (!p.aktiv || p.aktiv.toLowerCase() === 'j')
        )
      },
      aktionen() {
        return [
          { key: 'anzeigen', label: this.$t('global.show_c') },
          { key: 'zuweisen', label: this.$t('global.assign') },
          {
            key: 'publizieren',
            label: this.$t('global.assign') + ' & ' + this.$t('global.Publish'),
          },
        ]
      },
    },
    watch: {
      printout() {
        this.ausgewaehlteVariante = null
        if (this.mitDatum && this.printoutdaten.length === 0) this.ladePrintoutdaten()
      },
      varianten(val) {
        if (val.length === 1) this.ausgewaehlteVariante = val[0].id
      },
      // Default: alle Studienjahre, in denen schon Noten vorhanden sind
      bloecke: {
        immediate: true,
        handler(val) {
          this.ausgewaehlteBloecke = (val || [])
            .filter((b) => b.hatNoten)
            .map((b) => b.index)
        },
      },
    },
    methods: {
      ausgeben() {
        this.$emit('ok', {
          bloecke: [...this.ausgewaehlteBloecke].sort((a, b) => a - b),
          ohneNoteAnzeigen: this.ohneNoteAnzeigen,
          publish: PUBLISH[this.aktion],
          datum: this.datum,
          variante: this.ausgewaehlteVariante,
        })
        this.closeModal()
      },
      async ladePrintoutdaten() {
        try {
          const { data } = await CarboneApi.get('printoutdaten/')
          this.printoutdaten = Array.isArray(data) ? data : []
        } catch (e) {
          console.error('Fehler beim Laden der Printoutdaten', e)
        }
      },
    },
  }
</script>
